import { Add, Close } from '@project-lary/react-material-symbols';
import IconButton from 'gcs-common/design-system/ui/icon-button';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import SwipeableViews from 'react-swipeable-views';

interface AttachmentsPreviewProps {
  handleFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const AttachmentsPreview = (props: AttachmentsPreviewProps) => {
  const { handleFileChange } = props;
  const { control, setValue } = useFormContext();
  const attachments: File[] = useWatch({ control, name: 'attachments' }) || [];
  const [selectedFileIndex, setSelectedFileIndex] = useState<number>(0);

  const removeAttachment = (index: number): void => {
    const updatedFiles = attachments.filter((_, i) => i !== index);
    setValue('attachments', updatedFiles, { shouldValidate: true });

    if (updatedFiles.length === 0) {
      setSelectedFileIndex(0);
    } else if (selectedFileIndex >= updatedFiles.length) {
      setSelectedFileIndex(updatedFiles.length - 1);
    }
  };

  const isImage = (file: File): boolean => file.type.startsWith('image/');

  useEffect(() => {
    if (attachments.length > 0) {
      setSelectedFileIndex(attachments.length - 1);
    }
  }, [attachments]);

  return (
    attachments.length > 0 && (
      <div id="automated-qa-ticket-attachments-preview" className="flex h-full flex-grow flex-col">
        {/* File Header (Name & Remove Button) */}
        <div className="flex items-center justify-between bg-accent-9 p-2 text-white">
          <span className="max-w-[80%] truncate">{attachments[selectedFileIndex]?.name}</span>
          <IconButton
            Icon={Close}
            size="base"
            iconSize="text-lg"
            variant="ghost_neutral"
            onClick={() => removeAttachment(selectedFileIndex)}
          />
        </div>

        {/* Swipable Preview - Prevent Overflow */}
        <div className="flex h-full flex-grow items-center justify-center overflow-hidden">
          <SwipeableViews index={selectedFileIndex} onChangeIndex={setSelectedFileIndex} enableMouseEvents>
            {attachments.map((file: File, _: number) => (
              <div key={file.name} className="flex h-full w-full items-center justify-center">
                {isImage(file) ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt="preview"
                    className="h-auto max-h-[40vh] w-auto max-w-full rounded-md object-contain"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded-md bg-gray-200">
                    <span className="text-gray-700 text-sm">{file.name}</span>
                  </div>
                )}
              </div>
            ))}
          </SwipeableViews>
        </div>

        <div className="flex items-center gap-2 overflow-x-auto overflow-y-hidden bg-neutral-9 p-2">
          {attachments.map((file: File, index: number) => (
            <div
              key={file.name}
              role="button"
              tabIndex={0}
              className={`cursor-pointer rounded-md border-2 transition ${
                index === selectedFileIndex ? 'border-yellow-500' : 'border-transparent'
              }`}
              onClick={() => setSelectedFileIndex(index)}
            >
              {isImage(file) ? (
                <img src={URL.createObjectURL(file)} alt="thumb" className="h-12 w-12 rounded-md object-cover" />
              ) : (
                <div className="flex h-12 w-12 items-center justify-center bg-gray-300 text-xs">{file.name}</div>
              )}
            </div>
          ))}

          <label className="flex h-12 w-12 cursor-pointer items-center justify-center rounded-md bg-gray-300">
            <IconButton Icon={Add} size="base" iconSize="text-2xl" variant="ghost_neutral" />
            <input type="file" className="hidden" multiple onChange={handleFileChange} />
          </label>
        </div>
      </div>
    )
  );
};

export default AttachmentsPreview;
